import classNames from 'classnames';
import debounce from 'lodash.debounce';
import { PaddingOptions } from 'mapbox-gl';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import useResizeObserver from '@alltrails/core/hooks/useResizeObserver';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { SPACE_200, SPACE_300 } from '@alltrails/denali/tokens';
import useMap from '../../hooks/useMap';
import styles from './MapLayout.module.scss';

const arePaddingsEqual = (p1: PaddingOptions, p2: PaddingOptions) =>
  p1.bottom === p2.bottom && p1.left === p2.left && p1.right === p2.right && p1.top === p2.top;

type MapLayoutProps = {
  // Padding that is NOT from the elements rendered in this component, such as when the mobile drawer overlaps the bottom of the map in live-share
  additionalPadding?: Partial<PaddingOptions>;
  bottomCenterContent?: ReactNode;
  legends?: ReactNode;
  mapControls: ReactNode;
  onMapPaddingChange?: (padding: PaddingOptions) => void;
  pageControls?: ReactNode;
  renderChildren?: (mapPadding: PaddingOptions) => ReactNode; // For one-off contents that need to be aware of the map padding
  sidebar?: ReactNode;
  sidebarClassName?: string;
  sidebarCollapsed?: boolean;
  topCenterContent?: ReactNode;
  topLeftContent?: ReactNode;
};

// See https://docs.google.com/drawings/d/1ZMDq4spv-Q2-iTUufRxWoNE-wsNcck8819a-sl4tnuo/edit?usp=sharing for a visual diagram of the map layout.
const MapLayout = ({
  additionalPadding,
  bottomCenterContent,
  legends,
  mapControls,
  onMapPaddingChange,
  pageControls,
  renderChildren,
  sidebar,
  sidebarClassName,
  sidebarCollapsed,
  topCenterContent,
  topLeftContent
}: MapLayoutProps) => {
  const map = useMap();
  const isMobile = useIsMobileSizedScreen();

  const [bottomRightControlsWidth, setBottomRightControlsWidth] = useState(0);
  const [bottomRightControlsContainer, setBottomRightControlsContainer] = useState<HTMLElement | null>(null);
  const handleBottomRightControlsResize = useMemo(
    () =>
      debounce((entries: ResizeObserverEntry[]) => {
        const { width } = entries[0].target.getBoundingClientRect();
        setBottomRightControlsWidth(width);
      }, 100),
    []
  );
  const [observeBottomRightControls, disconnectBottomRightControls] = useResizeObserver(handleBottomRightControlsResize);

  useEffect(() => {
    if (bottomRightControlsContainer) {
      observeBottomRightControls(bottomRightControlsContainer);
    }

    return () => disconnectBottomRightControls();
  }, [observeBottomRightControls, bottomRightControlsContainer, disconnectBottomRightControls]);

  const [sidebarWidth, setSidebarWidth] = useState(0);
  const [sidebarContainer, setSidebarContainer] = useState<HTMLElement | null>(null);
  const handleSidebarResize = useMemo(
    () =>
      debounce((entries: ResizeObserverEntry[]) => {
        const { width } = entries[0].target.getBoundingClientRect();
        setSidebarWidth(sidebarCollapsed ? 0 : width);
        // The delay should be higher than the transition delay for the sidebar container so that this doesn't fire when the sidebar is expanded before the transition starts
      }, 250),
    [sidebarCollapsed]
  );
  const [observeSidebar, disconnectSidebar] = useResizeObserver(handleSidebarResize);

  useEffect(() => {
    if (sidebarContainer) {
      observeSidebar(sidebarContainer);
    } else {
      setSidebarWidth(0);
    }

    return () => disconnectSidebar();
  }, [sidebarContainer, observeSidebar, disconnectSidebar]);

  const containerPadding = useMemo(() => {
    const defaultContainerPadding = isMobile
      ? { top: SPACE_300, bottom: SPACE_300, left: SPACE_200, right: SPACE_200 }
      : { top: SPACE_300, bottom: SPACE_300, left: SPACE_300, right: SPACE_300 };
    if (additionalPadding) {
      return {
        top: defaultContainerPadding.top + (additionalPadding.top || 0),
        bottom: defaultContainerPadding.bottom + (additionalPadding.bottom || 0),
        left: defaultContainerPadding.left + (additionalPadding.left || 0),
        right: defaultContainerPadding.right + (additionalPadding.right || 0)
      };
    }
    return defaultContainerPadding;
  }, [additionalPadding, isMobile]);

  const mapPadding = useMemo(() => {
    return {
      top: containerPadding.top,
      bottom: containerPadding.bottom,
      left: containerPadding.left + sidebarWidth,
      right: containerPadding.right
    };
  }, [containerPadding, sidebarWidth]);

  useEffect(() => {
    if (map) {
      if (!arePaddingsEqual(mapPadding, map.getPadding())) {
        map.flyTo({ padding: mapPadding });
        onMapPaddingChange?.(mapPadding);
      }
    }
  }, [map, mapPadding, onMapPaddingChange]);

  const topCenterContainerStyle = useMemo(() => {
    if (isMobile) return undefined;
    return {
      top: mapPadding.top,
      left: mapPadding.left + (sidebarWidth > 0 ? SPACE_200 : 0),
      right: mapPadding.right
    };
  }, [isMobile, mapPadding.top, mapPadding.left, mapPadding.right, sidebarWidth]);

  const bottomCenterContainerStyle = useMemo(() => {
    // On mobile, the bottom center container is positioned relatively, not absolutely, so we don't need to set any styles
    if (isMobile) {
      return undefined;
    }
    return {
      bottom: mapPadding.bottom,
      left: mapPadding.left + (sidebarWidth > 0 ? SPACE_200 : 0),
      right: mapPadding.right + (bottomRightControlsWidth > 0 ? SPACE_200 : 0)
    };
  }, [isMobile, mapPadding.bottom, mapPadding.left, mapPadding.right, sidebarWidth, bottomRightControlsWidth]);

  return (
    <div
      className={styles.container}
      style={{
        paddingTop: containerPadding.top,
        paddingBottom: containerPadding.bottom,
        paddingLeft: containerPadding.left,
        paddingRight: containerPadding.right
      }}
    >
      {sidebar && !isMobile ? (
        <div className={classNames(styles.sidebarContainer, { [styles.collapsed]: sidebarCollapsed }, sidebarClassName)} ref={setSidebarContainer}>
          {sidebar}
        </div>
      ) : null}
      {topCenterContent ? (
        <div className={styles.topCenterContainer} style={topCenterContainerStyle}>
          {topCenterContent}
        </div>
      ) : null}
      <div className={styles.mainMapContent}>
        {topLeftContent ? (
          <div className={styles.topLeftContainer}>
            {topLeftContent}
          </div>
        ) : null}
        <div className={styles.controlsContainer}>
          <div className={styles.topRightControls}>{mapControls}</div>
          {pageControls ? (
            <div className={styles.bottomRightControls} ref={setBottomRightControlsContainer}>
              {pageControls}
            </div>
          ) : null}
        </div>
      </div>
      {bottomCenterContent || legends ? (
        <div className={styles.bottomCenterContainer} style={bottomCenterContainerStyle}>
          {legends}
          {bottomCenterContent}
        </div>
      ) : null}
      {renderChildren?.(mapPadding)}
    </div>
  );
};

export default MapLayout;
